import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form } from 'react-final-form';
import { FormApi } from 'final-form';

import { snackbar } from '@occc-app/design-system';
import { Flow_Types } from '@occc-app/offerHelpers/types';
import { OfferFragment, User_Role } from '@occc/graphql';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { getErrorMessage } from '@occc/utils';
import { useUserSelector } from '@occc-app/App/AppContext';
import { validateScheduleDatesForm } from '@occc-app/routes/CampaignComponents/components/ScheduleDates/schemas';
import { OfferForm } from '@occc-app/offerHelpers/components/OfferForm';
import { useExecuteOperations } from '@occc-app/offerHelpers/operations/useExecuteOperations';
import { getScheduleOperations } from '@occc-app/offerHelpers/ScheduleForm/getScheduleOperations';
import { transformOfferToScheduleValues } from '@occc-app/offerHelpers/ScheduleForm/transformOfferToScheduleInitialValues';

import { FinalizeFormContent } from './FinalizeFormContent';

import { routeCreator } from '../../utils';
import {
  FinalizeFormInitialValues,
  FinalizeFormProps,
  FinalizeFormRenderProps,
  FinalizeFormValues,
} from '../../types';

type FinalizeFormContainer = {
  award: OfferFragment;
  refetchAward: () => Promise<unknown>;
  setAllowNavigation?: (allowNavigation: boolean) => void;
  handleCancel: (hasUnsavedChanges?: boolean) => void;
  currentFlowType?: Flow_Types;
};

export const FinalizeFormContainer = ({
  award,
  refetchAward,
  setAllowNavigation,
  handleCancel,
  currentFlowType = Flow_Types.Update,
}: FinalizeFormContainer) => {
  const navigate = useNavigate();
  const currentUser = useUserSelector();

  const isReadOnlyRole = currentUser?.userRole === User_Role.AdminReadOnly;

  const [executeOperations, loading] = useExecuteOperations<FinalizeFormValues>({
    offerId: award.id,
    offer: award,
    getOperations: getScheduleOperations,
  });

  const onCompleted = () => {
    if (setAllowNavigation) {
      setAllowNavigation(true);
    }

    if (currentFlowType === Flow_Types.Configure) {
      navigate(routeCreator.details(award.id));
      snackbar.success('Award has been configured!');
      return;
    }
    snackbar.success('Award was updated.');
  };

  const initialValues: FinalizeFormInitialValues = useMemo(
    () => transformOfferToScheduleValues(award),
    [award],
  );

  const onSubmit = async (
    values: FinalizeFormValues,
    form: FormApi<FinalizeFormValues, FinalizeFormInitialValues>,
  ) => {
    try {
      await executeOperations(values, form.getState());
      await refetchAward();
      onCompleted();
    } catch (error) {
      if (error instanceof Error) {
        snackbar.error(getErrorMessage(error));
        return;
      }
      snackbar.error('Something went wrong. Please try again.');
    }
  };

  const handleNextButtonClick = useCallback(() => {
    navigate(routeCreator.details(award.id));
  }, [navigate, award.id]);

  const finalizeRenderForm: FinalizeFormProps['render'] = useCallback(
    ({
      handleSubmit,
      values,
      modifiedSinceLastSubmit,
      dirtySinceLastSubmit,
      dirty,
      submitting,
    }: FinalizeFormRenderProps) => {
      const isDirty = modifiedSinceLastSubmit ? dirtySinceLastSubmit : dirty;

      return (
        <OfferForm dirty={!submitting && isDirty} handleSubmit={handleSubmit}>
          <FinalizeFormContent
            values={values}
            isReadOnlyRole={isReadOnlyRole}
            handleCancel={() => handleCancel(isDirty)}
            handleNextButtonClick={handleNextButtonClick}
            currentFlowType={currentFlowType}
            submitting={submitting}
            dirty={isDirty}
            isLoading={loading}
          />
        </OfferForm>
      );
    },
    [isReadOnlyRole, handleNextButtonClick, currentFlowType, handleCancel, loading],
  );

  return (
    <Form<FinalizeFormValues>
      onSubmit={onSubmit}
      render={finalizeRenderForm}
      initialValues={initialValues}
      validate={validateScheduleDatesForm}
    />
  );
};
